import type { AnalyticsEvent } from '@listen-draw/shared';
import { storage } from './api.js';
import { currentLocale } from './i18n.js';

const CONSENT = 'ld.analytics';
const allowed = () => storage.get(CONSENT) === 'yes' && navigator.doNotTrack !== '1';
const visitorKey = 'ld.visitor';
const session = crypto.randomUUID();
const seen = new Set<string>();
type Properties = Pick<AnalyticsEvent, 'mode' | 'challenge' | 'referral' | 'correct' | 'pct'>;
const initialUrl = new URL(location.href);
const referral = initialUrl.searchParams.get('ref') ?? '';
const validRef = /^[a-z0-9]{8,24}$/.test(referral) ? referral : undefined;
const channel = initialUrl.searchParams.get('utm_source')?.toLowerCase();
let referrerHost = '';
try {
  referrerHost = new URL(document.referrer).hostname;
} catch {
  /* no referrer */
}
const source: AnalyticsEvent['source'] = validRef
  ? 'referral'
  : channel === 'producthunt' || /(^|\.)producthunt\.com$/.test(referrerHost)
    ? 'producthunt'
    : channel === 'x' || channel === 'twitter' || referrerHost === 't.co'
      ? 'x'
      : channel === 'youtube'
        ? 'youtube'
        : channel === 'instagram'
          ? 'instagram'
          : channel === 'community'
            ? 'community'
            : channel || referrerHost
              ? 'other'
              : 'direct';

export const analyticsEnabled = allowed;
export function setAnalyticsEnabled(value: boolean): void {
  storage.set(CONSENT, value ? 'yes' : 'no');
  if (!value) {
    storage.remove(visitorKey);
    storage.remove('ld.eventMarks');
    seen.clear();
  } else {
    track('landing_view', {}, 'landing');
    if (validRef) track('referral_visit', { referral: validRef }, 'referral');
  }
}
export function track(name: AnalyticsEvent['name'], props: Properties = {}, once?: string): void {
  if (!allowed()) return;
  if (once && seen.has(`${name}:${once}`)) return;
  if (once) seen.add(`${name}:${once}`);
  // Reconnecting/reloading a multiplayer result must not emit the same outcome twice.
  if (
    once &&
    props.mode === 'multiplayer' &&
    (name === 'round_complete' || name === 'host_activated')
  ) {
    let marks: Record<string, number> = {};
    try {
      marks = JSON.parse(storage.get('ld.eventMarks') ?? '{}') as Record<string, number>;
    } catch {
      /* reset malformed local data */
    }
    const key = `${name}:${once}`;
    if (marks && (marks[key] ?? 0) > Date.now() - 30 * 86400000) return;
    marks = Object.fromEntries(
      Object.entries(marks ?? {})
        .filter(([, at]) => typeof at === 'number' && at > Date.now() - 30 * 86400000)
        .slice(-299),
    );
    marks[key] = Date.now();
    storage.set('ld.eventMarks', JSON.stringify(marks));
  }
  let visitor: string | undefined;
  try {
    const stored = JSON.parse(storage.get(visitorKey) ?? 'null') as {
      id: string;
      until: number;
    } | null;
    if (stored && stored.until > Date.now() && /^[\da-f-]{36}$/.test(stored.id))
      visitor = stored.id;
  } catch {
    /* invalid storage */
  }
  if (!visitor) {
    visitor = crypto.randomUUID();
    storage.set(visitorKey, JSON.stringify({ id: visitor, until: Date.now() + 30 * 86400000 }));
  }
  const event: AnalyticsEvent = {
    id: crypto.randomUUID(),
    visitor,
    session,
    name,
    source,
    locale: currentLocale(),
    device: matchMedia('(pointer: coarse)').matches ? 'mobile' : 'desktop',
    ...props,
  };
  void fetch('/api/events', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(event),
    keepalive: true,
  }).catch(() => {
    /* metrics must never block play */
  });
}
export function trackArrival(): void {
  track('landing_view', {}, 'landing');
  if (validRef) track('referral_visit', { referral: validRef }, 'referral');
}
